/**
 * API-слой admin-app.
 *
 * Тонкие обёртки над `@hsc/api-client.customFetch`. Часть endpoint'ов уже есть
 * на backend (помечены REAL), часть — заглушки с пометкой TODO, пока бэкенд не
 * реализует соответствующий handler. Все вызовы идут под Bearer-токеном
 * platform_admin; на backend они защищены RequirePlatformAdminForPrefix.
 *
 * REAL (подтверждено в cmd/api + internal modules http):
 *   - POST /v1/auth/login                         — вход
 *   - GET  /v1/platform/me                        — профиль оператора (wire_me.go)
 *   - GET  /v1/admin/tenants                      — список отелей (admin_subscription)
 *   - PATCH /v1/admin/tenants/{id}/tier           — смена тарифа
 *   - POST /v1/admin/tenants/{id}/suspend         — приостановка
 *   - POST /v1/admin/tenants/{id}/owners          — привязка владельца
 *   - DELETE /v1/admin/tenants/{id}/owners/{uid}  — отвязка владельца
 *   - POST /v1/admin/tenants/{id}/extend-subscription          — продление
 *   - GET  /v1/admin/tenants/{id}/subscription-extensions      — история продлений
 *   - GET  /v1/admin/tenants/{id}/audit           — аудит-цепочка отеля
 *   - POST /v1/admin/provision-tenant-with-manager — онбординг (atomic provision)
 *   - GET  /v1/admin/workflows                    — Temporal workflows (process_engine)
 *   - GET/POST .../roles, .../users/{id}/roles    — RBAC (admin roles)
 *
 * TODO (endpoint'ы, которых ещё НЕТ на backend — нужны для admin-app):
 *   - GET  /v1/platform/dashboard                 — KPI дашборда (MRR/churn/alerts)
 *   - POST /v1/auth/2fa/verify                    — подтверждение TOTP при логине
 *   - POST /v1/auth/recovery/request|verify|reset — восстановление пароля (3 шага)
 *   - GET  /v1/platform/health                    — агрегированное здоровье платформы
 *   - GET  /v1/admin/onboarding/sagas             — очередь онбординга (resumable)
 *   - PATCH /v1/admin/tenants/{id}/capabilities   — переключение capability + reason
 *   - GET  /v1/admin/tenants/{id}/capabilities/history — история изменений capability
 *   - POST /v1/admin/impersonation                — step-up impersonation (2FA + reason)
 *   - POST /v1/admin/audit/verify                 — запуск проверки цепочки (cross-tenant)
 */
import { customFetch } from '@hsc/api-client';

/* ===================== Auth (вход + 2FA + восстановление) ================== */

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  /** true → требуется второй фактор (TOTP) перед выдачей полноценной сессии. */
  totp_required?: boolean;
  /** Временный токен для шага 2FA, если бэкенд использует промежуточный токен. */
  mfa_token?: string;
}

/** REAL: POST /v1/auth/login. */
export async function login(req: LoginRequest): Promise<LoginResponse> {
  return customFetch<LoginResponse>({ url: '/auth/login', method: 'POST', data: req });
}

export interface TotpVerifyRequest {
  mfa_token?: string;
  code: string;
}

/**
 * TODO(backend): POST /v1/auth/2fa/verify — подтверждение TOTP-кода и выдача
 * полноценного access_token. Сейчас endpoint'а нет — UI работает по потоку,
 * вызов вернёт ошибку до реализации. На время каркаса фронт замокан в тестах.
 */
export async function verifyTotp(req: TotpVerifyRequest): Promise<LoginResponse> {
  return customFetch<LoginResponse>({ url: '/auth/2fa/verify', method: 'POST', data: req });
}

/** TODO(backend): POST /v1/auth/recovery/request — отправка кода на email. */
export async function requestRecovery(email: string): Promise<{ ok: boolean }> {
  return customFetch<{ ok: boolean }>({
    url: '/auth/recovery/request',
    method: 'POST',
    data: { email },
  });
}

/** TODO(backend): POST /v1/auth/recovery/verify — проверка кода из письма. */
export async function verifyRecovery(req: {
  email: string;
  code: string;
}): Promise<{ reset_token: string }> {
  return customFetch<{ reset_token: string }>({
    url: '/auth/recovery/verify',
    method: 'POST',
    data: req,
  });
}

/** TODO(backend): POST /v1/auth/recovery/reset — установка нового пароля. */
export async function resetPassword(req: {
  reset_token: string;
  new_password: string;
}): Promise<{ ok: boolean }> {
  return customFetch<{ ok: boolean }>({
    url: '/auth/recovery/reset',
    method: 'POST',
    data: req,
  });
}

/* ===================== Профиль оператора ================== */

export interface PlatformMe {
  id: string;
  email: string;
  display_name?: string;
  roles?: string[];
}

/** REAL: GET /v1/platform/me (wire_me.go). */
export async function fetchPlatformMe(): Promise<PlatformMe> {
  return customFetch<PlatformMe>({ url: '/platform/me', method: 'GET' });
}

/* ===================== Онбординг отеля ================== */

export interface ProvisionTenantRequest {
  hotel_name: string;
  slug: string;
  manager_email: string;
  manager_first_name: string;
  manager_last_name: string;
  tier: string;
  capabilities: string[];
}

export interface ProvisionTenantResponse {
  tenant_id: string;
  slug: string;
  invitation_id?: string;
}

/**
 * REAL: POST /v1/admin/provision-tenant-with-manager — атомарное создание
 * tenant + приглашение управляющего. Используется на последнем шаге wizard'а
 * (Go-live). Промежуточные шаги wizard'а собирают данные на клиенте.
 */
export async function provisionTenantWithManager(
  req: ProvisionTenantRequest,
): Promise<ProvisionTenantResponse> {
  return customFetch<ProvisionTenantResponse>({
    url: '/admin/provision-tenant-with-manager',
    method: 'POST',
    data: req,
  });
}
